import React, { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { use2FA, useAppDispatch, useAppSelector, useFormattedTime, useMobile } from '@pkg/hooks';
import { TwoFactorAuthType } from '@pkg/types';
import { handleKeyDown } from '@pkg/utils';

import { apiFactory } from '@app/client';
import { Auth } from '@app/client/resources';
import config from '@app/config';
import { addAlert, hide2faExpiredLogin, set2faResetCode } from '@app/store/slices';
import { useMintingOverlayStore } from '@app/store/zustand';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

import SecurityCodeInput, { SecurityCodeInputVariants } from '../../Input/SecurityCodeInput';

import './ExpiredPopup.scss';

const RESEND_TIMEOUT = 120;
const { twoFACodeDigitCount } = config;

export const TwoFactorExpiredPopup = ({ on2faNext }: { on2faNext?: (token?: string) => void }) => {
    const dispatch = useAppDispatch();
    const { mintingInitiated, setMintingFailed } = useMintingOverlayStore();
    const { t } = useTranslation(['twoFactorAuth', 'words']);
    const { isMobile } = useMobile();
    const popupRef = useRef(null);
    const tokens = useAuthTokens((rootState) => rootState.token);
    const authUser = useAppSelector((rootState) => rootState.user);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [authCodes, setAuthCodes] = useState('');
    const [invalid, setInvalid] = useState(false);
    const { login2FA } = use2FA();
    const [isLoading, setIsLoading] = useState(false);

    const formattedTime = useFormattedTime(remainingSeconds);

    const onConfirm = useCallback(
        (event?: FormEvent) => {
            event?.preventDefault();
            if (!tokens) {
                return;
            }
            setIsLoading(true);
            login2FA({ code: authCodes, refreshToken: tokens.refresh_token, callback: on2faNext })
                .then((r) => {
                    if (!r.ok) {
                        setInvalid(true);
                    }
                })
                .catch((error) => {
                    if (error) {
                        setInvalid(true);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [tokens, login2FA, authCodes, on2faNext],
    );

    const onResend = () => {
        apiFactory()
            .call(Auth.sendSMS, {})
            .then((res) => {
                if (!res.ok) {
                    setInvalid(true);
                    dispatch(addAlert({ message: res.error as string }));
                    return;
                }
                setRemainingSeconds(RESEND_TIMEOUT);
                dispatch(set2faResetCode(true));
            });
    };

    const isSMS2FA = useMemo(() => {
        if (!authUser || !authUser.userAccount?.user.two_factor_auth) {
            return false;
        }
        return authUser.userAccount.user.two_factor_auth.includes(TwoFactorAuthType.SMS);
    }, [authUser]);

    const isValidAuthCodes = useMemo(() => {
        return authCodes.length === twoFACodeDigitCount;
    }, [authCodes]);

    useEffect(() => {
        if (!isMobile || !popupRef.current) {
            return;
        }

        const tl = gsap.timeline({
            repeat: 0,
            defaults: {
                duration: 0.5,
                delay: 0.2,
                ease: 'circ.easeIn',
            },
        });
        gsap.set(popupRef.current, { yPercent: 80, opacity: 0.3, delay: 0 });
        tl.addLabel('pushUp').fromTo(
            popupRef.current,
            { yPercent: 80, opacity: 0.3 },
            { yPercent: 0, opacity: 1 },
            'pushUp',
        );
    }, [isMobile, popupRef]);

    useEffect(() => {
        const timerId = setInterval(() => {
            setRemainingSeconds((r) => (r > 0 ? r - 1 : r));
        }, 1000);

        return () => {
            clearInterval(timerId);
        };
    }, []);

    const handleCancel = useCallback(() => {
        if (mintingInitiated) {
            setMintingFailed(true);
        }

        dispatch(hide2faExpiredLogin());
    }, [dispatch, mintingInitiated, setMintingFailed]);

    return (
        <>
            <div className="two-factor-expired-backdrop" />
            <div className="two-factor-expired-popup" ref={popupRef}>
                <div className="two-factor-expired-popup__container">
                    <div className="two-factor-expired-popup__container-content">
                        <h2 className="two-factor-expired-popup__title">
                            {t('2fa_step_setup_authenticator_enter_title')}
                        </h2>
                        <p className="two-factor-expired-popup__instruction">
                            <Trans
                                ns="twoFactorAuth"
                                i18nKey="2fa_step_setup_authenticator_enter_detail_authenticator"
                                components={{ br: <br /> }}
                            />
                        </p>
                        <form className="two-factor-expired-popup__form" onSubmit={(e) => onConfirm(e)}>
                            <div className="two-factor-expired-popup__input">
                                <SecurityCodeInput
                                    setCompleteCode={(code) => setAuthCodes(code)}
                                    isInvalid={invalid}
                                    digitCount={twoFACodeDigitCount}
                                    variant={SecurityCodeInputVariants.MidSpaced}
                                    digitOnly
                                    onAutoSubmit={onConfirm}
                                />
                            </div>
                            {isSMS2FA &&
                                (remainingSeconds === 0 ? (
                                    <p
                                        tabIndex={0}
                                        className="two-factor-expired-popup__send"
                                        onClick={onResend}
                                        onKeyDown={(e) => handleKeyDown(e, ['Enter', ' '], onResend)}
                                        aria-label={t('2fa_step_setup_authenticator_enter_resend')}
                                    >
                                        {t('2fa_step_setup_authenticator_enter_resend')}
                                    </p>
                                ) : (
                                    <p className="two-factor-expired-popup__send">
                                        {t('2fa_step_setup_authenticator_enter_resend_in', {
                                            timeString: formattedTime,
                                        })}
                                    </p>
                                ))}
                            <div className="two-factor-expired-popup__submit">
                                <Button
                                    color="secondary"
                                    disabled={!isValidAuthCodes}
                                    type="submit"
                                    loading={isLoading}
                                >
                                    {t('2fa_authenticate')}
                                </Button>
                            </div>
                        </form>
                        <div className="two-factor-expired-popup__cancel">
                            <Button color="clear" size={'small'} onClick={handleCancel}>
                                {t('words:cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
